<template>
    <div class="bw">

        <!-- Info banner -->
        <div class="bw-banner">
            <div class="bw-banner-icon">
                <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
                </svg>
            </div>
            <div>
                <p class="bw-banner-title">Decentralized BSC Withdrawal</p>
                <p class="bw-banner-sub">USDT (BEP-20) is sent from our hot wallet directly to your BSC address after admin approval. Funds are deducted from your E-Wallet balance immediately.</p>
            </div>
        </div>

        <!-- Form card -->
        <div class="bw-card">
            <div class="bw-card-head">
                <div class="bw-head-icon" :style="`background:rgba(${primaryRgb},0.15);border-color:rgba(${primaryRgb},0.3);`">
                    <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" :style="`color:${primaryColor};`">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
                    </svg>
                </div>
                <div>
                    <h3 class="bw-card-title">BSC USDT Withdrawal</h3>
                    <p class="bw-card-sub">BEP-20 Network · Min {{ currencySymbol }}{{ formatNum(gateway.min_amount) }} · Max {{ currencySymbol }}{{ formatNum(gateway.max_amount) }}</p>
                </div>
            </div>

            <form @submit.prevent="submitWithdrawal" class="bw-form">
                <!-- Amount -->
                <div class="bw-field">
                    <label class="bw-label">Withdrawal Amount (USDT)</label>
                    <div class="bw-amount-wrap">
                        <span class="bw-amount-prefix">$</span>
                        <input
                            v-model.number="form.amount"
                            type="number"
                            step="0.01"
                            :min="gateway.min_amount"
                            :max="Math.min(gateway.max_amount, walletBalance)"
                            class="bw-amount-input"
                            placeholder="0.00"
                            :disabled="isSubmitting"
                        />
                        <button type="button" class="bw-max-btn" @click="setMax" :style="`color:${primaryColor};border-color:rgba(${primaryRgb},0.35);background:rgba(${primaryRgb},0.08);`">MAX</button>
                    </div>
                    <p class="bw-hint">Available E-Wallet balance: <strong :style="`color:${primaryColor};`">{{ currencySymbol }}{{ formatNum(walletBalance) }}</strong></p>
                </div>

                <!-- BSC wallet address -->
                <div class="bw-field">
                    <label class="bw-label">Your BSC Wallet Address (BEP-20)</label>
                    <div class="bw-addr-wrap">
                        <input
                            v-model.trim="form.bscAddress"
                            type="text"
                            class="bw-input"
                            :class="{ 'bw-input--err': addressError }"
                            placeholder="0x..."
                            maxlength="42"
                            spellcheck="false"
                            :disabled="isSubmitting"
                            @input="validateAddress"
                        />
                        <div v-if="savedWallets && savedWallets.length" class="bw-book-btn-wrap">
                            <button type="button" class="bw-book-btn" :style="`color:${primaryColor};border-color:rgba(${primaryRgb},0.3);background:rgba(${primaryRgb},0.07);`" @click="showBook = !showBook">
                                <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>
                                Address Book
                            </button>
                        </div>
                    </div>
                    <p v-if="addressError" class="bw-field-err">{{ addressError }}</p>
                    <p v-else class="bw-hint">Must be a valid BSC (BEP-20) address starting with 0x.</p>

                    <!-- Address book dropdown -->
                    <div v-if="showBook && savedWallets && savedWallets.length" class="bw-book">
                        <div class="bw-book-title">Saved BSC Wallets</div>
                        <div
                            v-for="w in savedWallets"
                            :key="w.id"
                            class="bw-book-row"
                            @click="selectSavedWallet(w)"
                        >
                            <div class="bw-book-lbl">{{ w.label || 'Wallet' }}</div>
                            <div class="bw-book-addr">{{ truncateAddress(w.address) }}</div>
                        </div>
                    </div>
                </div>

                <!-- Fee Summary -->
                <div v-if="isValidAmount" class="bw-summary">
                    <div class="bw-sum-row">
                        <span class="bw-sum-lbl">Requested Amount</span>
                        <span class="bw-sum-val">${{ formatNum(form.amount) }} USDT</span>
                    </div>
                    <div v-if="fee > 0" class="bw-sum-row">
                        <span class="bw-sum-lbl">Network Fee</span>
                        <span class="bw-sum-val" style="color:#fbbf24;">-${{ formatNum(fee) }} USDT</span>
                    </div>
                    <div class="bw-sum-row bw-sum-total">
                        <span class="bw-sum-lbl--b">You Receive</span>
                        <span class="bw-sum-total-val" :style="`color:${primaryColor};`">${{ formatNum(netAmount) }} USDT</span>
                    </div>
                    <div class="bw-deduct">
                        <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="color:#f87171;flex-shrink:0;"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>
                        <span style="color:#94a3b8;font-size:11.5px;">Deducted from USDT balance: </span>
                        <span style="color:#f87171;font-weight:800;">${{ formatNum(form.amount) }}</span>
                    </div>
                </div>

                <!-- Submit -->
                <button
                    type="submit"
                    :disabled="!canSubmit || isSubmitting"
                    class="bw-submit"
                    :style="canSubmit
                        ? `background:linear-gradient(135deg,#7c3aed,${primaryColor});box-shadow:0 4px 18px rgba(${primaryRgb},0.35);`
                        : 'background:rgba(255,255,255,0.05);color:rgba(148,163,184,0.4);cursor:not-allowed;'"
                >
                    <svg v-if="isSubmitting" class="bw-spin" width="18" height="18" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    <svg v-else width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
                    </svg>
                    {{ isSubmitting ? 'Submitting...' : 'Submit Withdrawal Request' }}
                </button>

                <!-- Steps indicator -->
                <div class="bw-steps">
                    <div class="bw-step">
                        <div class="bw-step-num" :style="`background:rgba(${primaryRgb},0.15);color:${primaryColor};`">1</div>
                        <div>
                            <p class="bw-step-title">Submit Request</p>
                            <p class="bw-step-sub">USDT deducted from balance</p>
                        </div>
                    </div>
                    <div class="bw-step-arrow">→</div>
                    <div class="bw-step">
                        <div class="bw-step-num" style="background:rgba(251,191,36,0.15);color:#fbbf24;">2</div>
                        <div>
                            <p class="bw-step-title">Admin Review</p>
                            <p class="bw-step-sub">Usually within 24 hours</p>
                        </div>
                    </div>
                    <div class="bw-step-arrow">→</div>
                    <div class="bw-step">
                        <div class="bw-step-num" style="background:rgba(16,185,129,0.15);color:#10b981;">3</div>
                        <div>
                            <p class="bw-step-title">On-Chain Send</p>
                            <p class="bw-step-sub">USDT sent to your wallet</p>
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <!-- Withdrawal history -->
        <div v-if="withdrawals && withdrawals.data && withdrawals.data.length" class="bw-hist">
            <div class="bw-hist-head">
                <h4 class="bw-hist-title">BSC Withdrawal History</h4>
            </div>
            <div class="bw-hist-list">
                <div v-for="w in withdrawals.data" :key="w.id" class="bw-hist-row">
                    <div class="bw-hist-l">
                        <span class="bw-hist-dot" :style="`background:${statusColor(w.status)};`"></span>
                        <div>
                            <p class="bw-hist-trx">{{ w.trx }}</p>
                            <p class="bw-hist-date">{{ formatDate(w.created_at) }}</p>
                        </div>
                    </div>
                    <div class="bw-hist-r">
                        <p class="bw-hist-amt" :style="`color:${primaryColor};`">${{ formatNum(w.final_amount) }} USDT</p>
                        <span class="bw-hist-badge" :style="`background:${statusColor(w.status)}1a;border-color:${statusColor(w.status)}40;color:${statusColor(w.status)};`">{{ ucFirst(w.status) }}</span>
                        <a
                            v-if="w.blockchain_tx_hash"
                            :href="`https://bscscan.com/tx/${w.blockchain_tx_hash}`"
                            target="_blank"
                            rel="noopener"
                            class="bw-hist-scan"
                            :style="`color:${primaryColor};`"
                            title="View on BscScan"
                        >
                            <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                        </a>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { useToast } from '@/composables/useToast.js';

const props = defineProps({
    gateway: { type: Object, required: true },
    walletBalance: { type: [Number, String], default: 0 },
    savedWallets: { type: Array, default: () => [] },
    withdrawals: { type: Object, default: () => ({ data: [] }) },
});

const { showToast } = useToast();
const page = usePage();

const primaryColor = computed(() => page.props.primaryColor || '#86efac');
const currencySymbol = computed(() => page.props.currencySymbol || '$');
const primaryRgb = computed(() => {
    const hex = primaryColor.value.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `${r}, ${g}, ${b}`;
});

const form = ref({ amount: null, bscAddress: '' });
const addressError = ref('');
const isSubmitting = ref(false);
const showBook = ref(false);

const isValidAddress = computed(() => /^0x[a-fA-F0-9]{40}$/.test(form.value.bscAddress));
const isValidAmount = computed(() => {
    const a = parseFloat(form.value.amount);
    if (isNaN(a) || a <= 0) return false;
    const min = parseFloat(props.gateway.min_amount || 0);
    const max = parseFloat(props.gateway.max_amount || Infinity);
    const bal = parseFloat(props.walletBalance || 0);
    return a >= min && a <= max && a <= bal;
});

const fee = computed(() => {
    if (!isValidAmount.value) return 0;
    const a = parseFloat(form.value.amount);
    const fixed = parseFloat(props.gateway.fixed_charge || 0);
    const pct   = parseFloat(props.gateway.percent_charge || 0);
    return fixed + (a * pct / 100);
});

const netAmount = computed(() => Math.max(0, parseFloat(form.value.amount || 0) - fee.value));
const canSubmit = computed(() => isValidAmount.value && isValidAddress.value && !addressError.value && !isSubmitting.value);

function validateAddress() {
    const addr = form.value.bscAddress.trim();
    if (!addr) {
        addressError.value = '';
        return;
    }
    if (!/^0x[a-fA-F0-9]{40}$/.test(addr)) {
        addressError.value = 'Invalid BSC address — must be 0x followed by 40 hex characters.';
    } else {
        addressError.value = '';
    }
}

function setMax() {
    const max = Math.min(parseFloat(props.gateway.max_amount || 0), parseFloat(props.walletBalance || 0));
    form.value.amount = Math.floor(max * 100) / 100;
}

function selectSavedWallet(wallet) {
    form.value.bscAddress = wallet.address;
    addressError.value = '';
    showBook.value = false;
}

function truncateAddress(addr) {
    if (!addr || addr.length < 12) return addr;
    return addr.slice(0, 6) + '...' + addr.slice(-4);
}

function formatNum(n) {
    const num = parseFloat(n);
    if (isNaN(num)) return '0.00';
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(num);
}

function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusColor(s) {
    switch ((s || '').toLowerCase()) {
        case 'approved': case 'confirmed': return '#10b981';
        case 'pending':  case 'sent':      return '#fbbf24';
        case 'rejected': case 'failed':    return '#ef4444';
        default: return '#94a3b8';
    }
}

function ucFirst(s) {
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
}

async function submitWithdrawal() {
    if (!canSubmit.value) return;
    isSubmitting.value = true;

    router.post('/user/wallet/withdraw', {
        withdrawal_gateway_id: props.gateway.id,
        amount: form.value.amount,
        balance_source: 'ewallet',
        withdrawal_details: {
            bsc_wallet_address: form.value.bscAddress,
        },
    }, {
        onSuccess: () => {
            showToast('BSC withdrawal request submitted! Pending admin approval.', 'success');
            form.value = { amount: null, bscAddress: '' };
            addressError.value = '';
        },
        onError: (errors) => {
            const msgs = Object.values(errors).flat();
            showToast(msgs[0] || 'Failed to submit withdrawal.', 'error');
        },
        preserveScroll: true,
        onFinish: () => { isSubmitting.value = false; },
    });
}
</script>

<style scoped>
.bw { display: flex; flex-direction: column; gap: 20px; }

/* ── Banner ── */
.bw-banner {
    display: flex; align-items: flex-start; gap: 14px;
    padding: 14px 18px;
    background: rgba(96,165,250,0.06);
    border: 1px solid rgba(96,165,250,0.2);
    border-radius: 14px;
}
.bw-banner-icon { color: #60a5fa; flex-shrink: 0; margin-top: 2px; }
.bw-banner-title { font-size: 13px; font-weight: 800; color: #60a5fa; margin: 0 0 4px; }
.bw-banner-sub { font-size: 12px; color: rgba(148,163,184,0.7); margin: 0; line-height: 1.5; }

/* ── Card ── */
.bw-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.14);
    border-radius: 20px;
    padding: 24px;
    position: relative;
    overflow: hidden;
}
.bw-card::before {
    content: '';
    position: absolute;
    top: 0; left: 10%; right: 10%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.4), transparent);
    pointer-events: none;
}
.bw-card-head { display: flex; align-items: center; gap: 14px; margin-bottom: 24px; }
.bw-head-icon { width: 46px; height: 46px; border-radius: 13px; border: 1px solid; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.bw-card-title { font-size: 16px; font-weight: 800; color: #fff; margin: 0 0 4px; }
.bw-card-sub { font-size: 11.5px; color: rgba(148,163,184,0.55); margin: 0; }

/* ── Form ── */
.bw-form { display: flex; flex-direction: column; gap: 18px; }
.bw-field { display: flex; flex-direction: column; gap: 7px; position: relative; }
.bw-label { font-size: 12.5px; font-weight: 700; color: rgba(226,232,240,0.85); }
.bw-hint { font-size: 11px; color: rgba(148,163,184,0.45); margin: 0; }
.bw-field-err { font-size: 11.5px; color: #f87171; margin: 0; font-weight: 600; }

/* Amount */
.bw-amount-wrap { display: flex; align-items: center; border: 1px solid rgba(255,255,255,0.1); border-radius: 11px; background: rgba(255,255,255,0.04); overflow: hidden; }
.bw-amount-prefix { padding: 0 12px; font-size: 15px; font-weight: 700; color: rgba(148,163,184,0.5); flex-shrink: 0; }
.bw-amount-input { flex: 1; padding: 12px 0; background: transparent; border: none; color: #fff; font-size: 18px; font-weight: 800; outline: none; min-width: 0; }
.bw-amount-input::placeholder { color: rgba(148,163,184,0.3); font-size: 16px; font-weight: 600; }
.bw-max-btn { padding: 6px 14px; margin: 6px; border-radius: 8px; border: 1px solid; font-size: 11px; font-weight: 800; cursor: pointer; background: transparent; transition: all 0.18s; letter-spacing: 0.04em; }
.bw-max-btn:hover { filter: brightness(1.2); }

/* Address */
.bw-addr-wrap { display: flex; flex-direction: column; gap: 8px; }
.bw-input {
    width: 100%; padding: 11px 14px; border-radius: 11px;
    border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.04);
    color: #fff; font-size: 13px; font-weight: 500; outline: none; font-family: monospace;
    transition: border-color 0.18s; box-sizing: border-box;
}
.bw-input:focus { border-color: rgba(167,93,237,0.45); }
.bw-input--err { border-color: rgba(239,68,68,0.5) !important; }
.bw-input::placeholder { color: rgba(148,163,184,0.35); font-family: inherit; }
.bw-book-btn-wrap { display: flex; }
.bw-book-btn { display: flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 9px; border: 1px solid; font-size: 12px; font-weight: 700; cursor: pointer; background: transparent; transition: all 0.18s; }
.bw-book-btn:hover { filter: brightness(1.15); }

/* Address book dropdown */
.bw-book {
    background: rgba(10,4,22,0.98);
    border: 1px solid rgba(167,93,237,0.25);
    border-radius: 12px;
    overflow: hidden;
}
.bw-book-title { padding: 9px 14px; font-size: 10.5px; font-weight: 700; color: rgba(148,163,184,0.5); text-transform: uppercase; letter-spacing: 0.07em; border-bottom: 1px solid rgba(255,255,255,0.06); }
.bw-book-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; cursor: pointer; transition: background 0.15s; }
.bw-book-row:hover { background: rgba(167,93,237,0.06); }
.bw-book-lbl { font-size: 12.5px; font-weight: 700; color: #fff; }
.bw-book-addr { font-size: 11.5px; font-weight: 600; color: rgba(148,163,184,0.6); font-family: monospace; }

/* Summary */
.bw-summary {
    background: rgba(255,255,255,0.025);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 14px;
    padding: 16px;
    display: flex; flex-direction: column; gap: 10px;
}
.bw-sum-row { display: flex; justify-content: space-between; align-items: center; }
.bw-sum-lbl { font-size: 12.5px; color: rgba(148,163,184,0.6); font-weight: 600; }
.bw-sum-val { font-size: 13px; font-weight: 700; color: #fff; }
.bw-sum-total { padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.07); }
.bw-sum-lbl--b { font-size: 13px; font-weight: 800; color: #fff; }
.bw-sum-total-val { font-size: 20px; font-weight: 900; }
.bw-deduct { display: flex; align-items: center; gap: 7px; padding: 10px 12px; border-radius: 9px; background: rgba(239,68,68,0.06); border: 1px solid rgba(239,68,68,0.18); }

/* Submit */
.bw-submit {
    width: 100%; padding: 13px 20px; border-radius: 12px; border: none;
    font-size: 14px; font-weight: 800; cursor: pointer; color: #0f172a;
    display: flex; align-items: center; justify-content: center; gap: 9px;
    transition: all 0.2s ease; letter-spacing: 0.02em;
}
.bw-submit:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.08); }
.bw-spin { animation: bwSpin 1s linear infinite; }
@keyframes bwSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* Steps */
.bw-steps { display: flex; align-items: flex-start; gap: 10px; padding-top: 4px; }
.bw-step { display: flex; align-items: flex-start; gap: 10px; flex: 1; }
.bw-step-num { width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 900; flex-shrink: 0; margin-top: 1px; }
.bw-step-title { font-size: 12px; font-weight: 700; color: #fff; margin: 0 0 2px; }
.bw-step-sub { font-size: 10.5px; color: rgba(148,163,184,0.45); margin: 0; }
.bw-step-arrow { font-size: 14px; color: rgba(148,163,184,0.3); margin-top: 4px; flex-shrink: 0; }

/* History */
.bw-hist { background: rgba(10,4,22,0.97); border: 1px solid rgba(167,93,237,0.14); border-radius: 16px; overflow: hidden; }
.bw-hist-head { padding: 14px 18px; border-bottom: 1px solid rgba(255,255,255,0.06); }
.bw-hist-title { font-size: 13.5px; font-weight: 800; color: #fff; margin: 0; }
.bw-hist-list { display: flex; flex-direction: column; }
.bw-hist-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.04); transition: background 0.15s; }
.bw-hist-row:last-child { border-bottom: none; }
.bw-hist-row:hover { background: rgba(167,93,237,0.03); }
.bw-hist-l { display: flex; align-items: center; gap: 10px; min-width: 0; }
.bw-hist-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.bw-hist-trx { font-size: 12px; font-weight: 700; color: #fff; margin: 0 0 2px; font-family: monospace; }
.bw-hist-date { font-size: 10.5px; color: rgba(148,163,184,0.45); margin: 0; }
.bw-hist-r { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.bw-hist-amt { font-size: 13px; font-weight: 800; margin: 0; }
.bw-hist-badge { font-size: 10px; font-weight: 700; padding: 2px 9px; border-radius: 50px; border: 1px solid; white-space: nowrap; }
.bw-hist-scan { display: flex; align-items: center; opacity: 0.7; transition: opacity 0.18s; }
.bw-hist-scan:hover { opacity: 1; }

@media (max-width: 640px) {
    .bw-steps { flex-direction: column; gap: 8px; }
    .bw-step-arrow { display: none; }
}
</style>
